import {
	ArrowDown,
	ArrowUp,
	CheckCircle2,
	Copy,
	ExternalLink,
	MessageCircle,
	Plus,
	Rocket,
	Sparkles,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { ThumbnailUpload } from "@/components/ui/thumbnail-upload";
import {
	Eyebrow,
	FloatPanel,
	Pill,
	type PillTone,
	StudioButton,
} from "@/features/studio/theme";
import {
	type DesignerPortfolio,
	getMyPortfolio,
	listMyPortfolios,
	type PortfolioProject,
	type PortfolioReviewStatus,
	type PortfolioTemplate,
	publishMyPortfolio,
	saveMyPortfolio,
	unpublishMyPortfolio,
} from "../services/portfolio-api.service";
import {
	PORTFOLIO_TEMPLATES,
	renderLiveTemplatePreview,
	renderMiniTemplatePreview,
	SelectedTemplateBadge,
	TemplateDeltaNotice,
	ViewLayoutHint,
} from "../template-definitions";

const REVIEW_STATUS_TONE: Record<PortfolioReviewStatus, PillTone> = {
	draft: "neutral",
	pending_review: "awaiting",
	approved: "done",
	rejected: "risk",
};

type RetractAction = "withdraw" | "unpublish";

/**
 * What a portfolio with no title is called. Leaving the title blank is allowed,
 * so this is the field's placeholder: it shows the designer the same words the
 * admin review queue already falls back to in its Title column
 * (`apps/admin/src/features/approvals/components/portfolio-review-queue.tsx`).
 */
const UNTITLED_PORTFOLIO = "Untitled portfolio";

/** Matches `savePortfolioSchema`'s `title.max(200)` and the varchar(200) column. */
const PORTFOLIO_TITLE_MAX_LENGTH = 200;

function ReviewStatusPill({
	status,
}: {
	status: PortfolioReviewStatus;
}): React.ReactElement {
	const { t } = useTranslation();
	return (
		<Pill tone={REVIEW_STATUS_TONE[status]} dot>
			{t(`designerPortfolio.review.status.${status}`)}
		</Pill>
	);
}

function emptyProject(): PortfolioProject {
	return {
		title: "",
		shortDescription: "",
		fullDescription: "",
		category: "",
		completionDate: "",
		thumbnailUrl: "",
		imageUrls: [],
	};
}

function TemplateCard({
	id,
	title,
	description,
	selected,
	projects,
	onClick,
}: {
	id: PortfolioTemplate;
	title: string;
	description: string;
	selected: boolean;
	projects: PortfolioProject[];
	onClick: () => void;
}): React.ReactElement {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				position: "relative",
				borderRadius: 11,
				border: selected
					? "2px solid var(--color-dashboard-accent)"
					: "1px solid var(--color-dashboard-border)",
				padding: 16,
				textAlign: "left",
				background: selected
					? "var(--color-dashboard-warm-100)"
					: "var(--color-dashboard-paper)",
				transition: "all 200ms ease",
				cursor: "pointer",
				display: "flex",
				flexDirection: "column",
				width: "100%",
			}}
		>
			{selected ? (
				<CheckCircle2
					style={{
						position: "absolute",
						right: 12,
						top: 12,
						height: 20,
						width: 20,
						color: "var(--color-dashboard-accent-dark)",
					}}
				/>
			) : null}
			<div style={{ marginBottom: 12 }}>
				<p
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 600,
						fontSize: 14.5,
						color: "var(--color-dashboard-text)",
					}}
				>
					{title}
				</p>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 11.5,
						color: "var(--color-dashboard-muted)",
						marginTop: 2,
					}}
				>
					{description}
				</p>
			</div>
			<div style={{ width: "100%", overflow: "hidden" }}>
				{renderMiniTemplatePreview(id, projects)}
			</div>
		</button>
	);
}

function ReorderProjectButtons({
	index,
	canMoveUp,
	canMoveDown,
	onMoveUp,
	onMoveDown,
}: {
	index: number;
	canMoveUp: boolean;
	canMoveDown: boolean;
	onMoveUp: () => void;
	onMoveDown: () => void;
}): React.ReactElement {
	return (
		<div style={{ display: "flex", gap: 6 }}>
			<button
				type="button"
				onClick={onMoveUp}
				disabled={!canMoveUp}
				aria-label={`Move up project ${index + 1}`}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 32,
					height: 32,
					borderRadius: 6,
					border: "1px solid var(--color-dashboard-border)",
					background: "var(--color-dashboard-paper)",
					cursor: canMoveUp ? "pointer" : "not-allowed",
					opacity: canMoveUp ? 1 : 0.4,
				}}
			>
				<ArrowUp
					className="h-4 w-4"
					style={{ color: "var(--color-dashboard-text)" }}
				/>
			</button>
			<button
				type="button"
				onClick={onMoveDown}
				disabled={!canMoveDown}
				aria-label={`Move down project ${index + 1}`}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: 32,
					height: 32,
					borderRadius: 6,
					border: "1px solid var(--color-dashboard-border)",
					background: "var(--color-dashboard-paper)",
					cursor: canMoveDown ? "pointer" : "not-allowed",
					opacity: canMoveDown ? 1 : 0.4,
				}}
			>
				<ArrowDown
					className="h-4 w-4"
					style={{ color: "var(--color-dashboard-text)" }}
				/>
			</button>
		</div>
	);
}

export function ManagePortfolioPage(): React.ReactElement {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();

	// A designer has exactly one portfolio. This list is now read for nothing but
	// the id the resolve below puts in the URL; the title the masthead edits
	// comes from `getMyPortfolio`, which is also what the save writes back.
	const [portfolios, setPortfolios] = useState<
		Array<
			DesignerPortfolio & {
				isPrimary: boolean;
				createdAt: string;
			}
		>
	>([]);
	const [resolveError, setResolveError] = useState<string | null>(null);
	const editingPortfolioId = searchParams.get("editPortfolioId");
	/**
	 * Ids `getMyPortfolio` came back empty for. The resolve below must not pick
	 * one again: clearing an id and re-selecting the same one would load it,
	 * fail, clear it and re-select it forever.
	 */
	const [unresolvableIds, setUnresolvableIds] = useState<string[]>([]);

	// Active editor state
	const [title, setTitle] = useState("");
	const [savedTitle, setSavedTitle] = useState("");
	const [template, setTemplate] = useState<PortfolioTemplate>("template_a");
	const [savedTemplate, setSavedTemplate] =
		useState<PortfolioTemplate>("template_a");
	const [projects, setProjects] = useState<PortfolioProject[]>([]);
	const [savedProjects, setSavedProjects] = useState<PortfolioProject[]>([]);
	const [_portfolioId, setPortfolioId] = useState<string | null>(null);
	const [reviewStatus, setReviewStatus] =
		useState<PortfolioReviewStatus>("draft");
	const [reviewNotes, setReviewNotes] = useState<string | null>(null);
	const [slug, setSlug] = useState<string | undefined>();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [publishing, setPublishing] = useState(false);
	const [confirmRetract, setConfirmRetract] = useState<RetractAction | null>(
		null,
	);

	/**
	 * Puts the portfolio being edited in the URL. The navigation replaces the
	 * current history entry so that landing on a bare `/portfolio` and being
	 * sent to `?editPortfolioId=<id>` leaves one entry behind, not two.
	 */
	const selectPortfolio = useCallback(
		(id: string) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set("editPortfolioId", id);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	/**
	 * Takes the portfolio out of the URL, so the resolve effect runs again
	 * against the already-loaded list. Replaces rather than pushes for the same
	 * reason `selectPortfolio` does: a dead id and the portfolio it is replaced
	 * with are one arrival, not two history entries.
	 */
	const clearSelectedPortfolio = useCallback(() => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("editPortfolioId");
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const hasUnsavedChanges = useMemo(() => {
		if (!editingPortfolioId) return false;
		if (title.trim() !== savedTitle) return true;
		if (template !== savedTemplate) return true;
		return JSON.stringify(projects) !== JSON.stringify(savedProjects);
	}, [
		editingPortfolioId,
		title,
		savedTitle,
		template,
		savedTemplate,
		projects,
		savedProjects,
	]);

	useEffect(() => {
		if (!hasUnsavedChanges) return;

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue =
				"You have unsaved changes. Are you sure you want to leave?";
			return e.returnValue;
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [hasUnsavedChanges]);

	const sharePath = useMemo(() => {
		if (!slug) return "";
		return `${window.location.origin}/portfolio/${slug}`;
	}, [slug]);

	// The name the share message gives this portfolio: the saved title, not the
	// half-typed one, and the same fallback the admin queue uses.
	const portfolioTitle = savedTitle || UNTITLED_PORTFOLIO;

	// Load the designer's portfolios once. This is what the resolve below reads
	// the portfolio id out of.
	useEffect(() => {
		let cancelled = false;
		async function loadList() {
			const data = await listMyPortfolios();
			if (cancelled) return;
			if (!data || data.portfolios.length === 0) {
				setResolveError(
					"We could not open your portfolio. Please reload the page.",
				);
				setLoading(false);
				return;
			}
			setPortfolios(data.portfolios);
		}
		void loadList();
		return () => {
			cancelled = true;
		};
	}, []);

	// Land straight in the editor. `listMyPortfolios` auto-creates a portfolio
	// for a designer who has none and returns them ordered `isPrimary` first
	// then oldest first, so the first entry that is still loadable is the one
	// portfolio this page edits — a designer whose only portfolio is not flagged
	// primary included.
	useEffect(() => {
		if (editingPortfolioId || portfolios.length === 0) return;
		const next = portfolios.find((one) => !unresolvableIds.includes(one.id));
		if (!next) {
			setResolveError(
				"We could not open your portfolio. Please reload the page.",
			);
			setLoading(false);
			return;
		}
		selectPortfolio(next.id);
	}, [editingPortfolioId, portfolios, unresolvableIds, selectPortfolio]);

	// Load specific portfolio details when editing
	useEffect(() => {
		if (!editingPortfolioId) return;
		const requestedId = editingPortfolioId;
		async function loadDetail() {
			setLoading(true);
			const data = await getMyPortfolio(requestedId);
			if (data) {
				setTitle(data.portfolio.title ?? "");
				setSavedTitle(data.portfolio.title ?? "");
				setTemplate(data.portfolio.template);
				setSavedTemplate(data.portfolio.template);
				const initialProjects =
					data.projects.length > 0 ? data.projects : [emptyProject()];
				setProjects(initialProjects);
				setSavedProjects(initialProjects);
				setPortfolioId(data.portfolio.id);
				setReviewStatus(data.portfolio.reviewStatus);
				setReviewNotes(data.portfolio.reviewNotes);
				setSlug(data.portfolio.slug);
				setLoading(false);
				return;
			}
			// Nothing came back for this id: it is not this designer's, or it no
			// longer exists. Rendering an editor over it showed a bookmarked or
			// shared URL as an empty portfolio. Drop it instead and let the resolve
			// above land on the designer's real one; if the list did not load
			// either, that effect reports the failure and the alert takes over.
			setUnresolvableIds((prev) =>
				prev.includes(requestedId) ? prev : [...prev, requestedId],
			);
			clearSelectedPortfolio();
		}
		void loadDetail();
	}, [editingPortfolioId, clearSelectedPortfolio]);

	function updateProject(index: number, patch: Partial<PortfolioProject>) {
		setProjects((prev) =>
			prev.map((project, i) =>
				i === index ? { ...project, ...patch } : project,
			),
		);
	}

	function addProject() {
		setProjects((prev) => [...prev, emptyProject()]);
	}

	function removeProject(index: number) {
		setProjects((prev) => {
			const next = prev.filter((_, i) => i !== index);
			return next.length > 0 ? next : [emptyProject()];
		});
	}

	function moveProject(index: number, direction: "up" | "down") {
		setProjects((prev) => {
			const swapWith = direction === "up" ? index - 1 : index + 1;
			if (swapWith < 0 || swapWith >= prev.length) {
				return prev;
			}

			const next = [...prev];
			const current = next[index];
			next[index] = next[swapWith];
			next[swapWith] = current;
			return next;
		});
	}

	/**
	 * Saves the editor's current title, template and projects, and reports
	 * whether the save went through. `announce` controls the "Portfolio saved"
	 * toast, which the submit path leaves to its own toast.
	 */
	async function savePortfolio({
		announce,
	}: {
		announce: boolean;
	}): Promise<boolean> {
		if (!editingPortfolioId) return false;
		setSaving(true);
		// A blank title is a legitimate save: it clears the stored one and the
		// portfolio goes back to being shown as "Untitled portfolio".
		const trimmedTitle = title.trim();
		const result = await saveMyPortfolio(
			{ title: trimmedTitle, template, projects },
			editingPortfolioId,
		);
		setSaving(false);

		if (!result.success) {
			toast.error(result.error || "Failed to save portfolio");
			return false;
		}

		if (announce) toast.success("Portfolio saved");
		const storedTitle = result.portfolio
			? (result.portfolio.title ?? "")
			: trimmedTitle;
		setTitle(storedTitle);
		setSavedTitle(storedTitle);
		setSavedTemplate(template);
		if (result.projects) {
			setProjects(result.projects);
			setSavedProjects(result.projects);
		} else {
			setSavedProjects(projects);
		}
		return true;
	}

	async function handleSave() {
		await savePortfolio({ announce: true });
	}

	/**
	 * Shows the review state the server returned, or `expected` when the
	 * response carries no portfolio. The success toast is shown only when that
	 * state is `expected`; any other state is announced by its label.
	 */
	function applyReviewResult(
		portfolio: DesignerPortfolio | undefined,
		expected: PortfolioReviewStatus,
		successMessage: string,
	) {
		const next = portfolio?.reviewStatus ?? expected;
		setReviewStatus(next);
		setReviewNotes(portfolio?.reviewNotes ?? null);
		if (next === expected) {
			toast.success(successMessage);
		} else {
			toast.info(
				t("designerPortfolio.review.statusToast", {
					status: t(`designerPortfolio.review.status.${next}`),
				}),
			);
		}
	}

	async function handleSubmitForReview() {
		if (!editingPortfolioId) return;
		// The server reviews what it has stored, so unsaved edits would be left
		// out of the submission -- or, on a portfolio whose projects were never
		// saved, refused with "Add at least one project before publishing".
		// Busy from the first click, so the gap between the save finishing and
		// the publish starting cannot take a second submit.
		setPublishing(true);
		let result: Awaited<ReturnType<typeof publishMyPortfolio>>;
		try {
			if (hasUnsavedChanges && !(await savePortfolio({ announce: false }))) {
				return;
			}
			result = await publishMyPortfolio(editingPortfolioId);
		} finally {
			setPublishing(false);
		}

		if (!result.success) {
			toast.error(result.error ?? t("designerPortfolio.review.submitError"));
			return;
		}

		if (result.shareUrlPath) {
			setSlug(result.shareUrlPath.split("/").pop());
		}
		applyReviewResult(
			result.portfolio,
			"pending_review",
			t("designerPortfolio.review.submittedToast"),
		);
	}

	async function handleRetract(action: RetractAction) {
		if (!editingPortfolioId) return;
		setPublishing(true);
		const result = await unpublishMyPortfolio(editingPortfolioId);
		setPublishing(false);

		if (!result.success) {
			toast.error(
				result.error ??
					t(
						action === "withdraw"
							? "designerPortfolio.review.withdrawError"
							: "designerPortfolio.review.unpublishError",
					),
			);
			return;
		}

		applyReviewResult(
			result.portfolio,
			"draft",
			t(
				action === "withdraw"
					? "designerPortfolio.review.withdrawnToast"
					: "designerPortfolio.review.unpublishedToast",
			),
		);
	}

	async function copyShareLink() {
		if (!sharePath) return;
		await navigator.clipboard.writeText(sharePath);
		toast.success("Share link copied");
	}

	const loadingScreen = (
		<div
			style={{
				padding: "80px 40px",
				textAlign: "center",
				fontFamily: "var(--font-body)",
				color: "var(--color-dashboard-muted)",
			}}
		>
			Loading portfolio studio...
		</div>
	);

	// Until the portfolio is resolved into the URL there is nothing to edit, so
	// the page waits here rather than offering a list. A resolve failure is
	// reported only in this state: once the editor is up, a later list failure
	// must not take it away.
	if (!editingPortfolioId) {
		if (!resolveError) return loadingScreen;
		return (
			<div
				role="alert"
				style={{
					padding: "80px 40px",
					textAlign: "center",
					fontFamily: "var(--font-body)",
					color: "var(--color-dashboard-danger)",
				}}
			>
				{resolveError}
			</div>
		);
	}

	if (loading) {
		return loadingScreen;
	}

	// ---------------------------------------------------------------------------
	// EDITOR: the designer's one portfolio and its projects
	// ---------------------------------------------------------------------------

	return (
		<div className="min-h-screen bg-transparent p-4 sm:p-12">
			<div className="mx-auto max-w-6xl space-y-8">
				{/* Top Navigation / Masthead Panel */}
				<FloatPanel elevation={1} padding={32}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 24,
						}}
					>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								justifyContent: "space-between",
								gap: 20,
							}}
						>
							<div style={{ maxWidth: "100%" }}>
								<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
									<p className="inline-flex items-center gap-2 rounded-full bg-dashboard-warm-350 px-3 py-1 text-xs font-semibold text-[var(--color-dashboard-accent-dark)]">
										<Sparkles className="h-3.5 w-3.5" />
										PORTFOLIO EDITOR
									</p>
									<span data-testid="portfolio-review-status">
										<ReviewStatusPill status={reviewStatus} />
									</span>
								</div>
								<h1
									style={{
										fontFamily: "var(--font-display)",
										fontWeight: 500,
										fontSize: 36,
										color: "var(--color-dashboard-text)",
										letterSpacing: "-0.02em",
										marginTop: 8,
									}}
								>
									Editing:{" "}
									<input
										type="text"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										placeholder={UNTITLED_PORTFOLIO}
										maxLength={PORTFOLIO_TITLE_MAX_LENGTH}
										aria-label="Portfolio title"
										// The save writes the stored title back into this field when
										// it returns, so anything typed while it was in flight would
										// be silently discarded. Refusing the keystrokes is the
										// honest half of that: nothing is lost that was accepted.
										disabled={saving}
										style={{
											fontFamily: "var(--font-display)",
											fontStyle: "italic",
											fontSize: "inherit",
											fontWeight: "inherit",
											letterSpacing: "inherit",
											color: "var(--color-dashboard-accent-display)",
											background: "none",
											border: "none",
											borderBottom: "1px dashed var(--color-border-default)",
											borderRadius: 0,
											outline: "none",
											padding: "0 2px",
											cursor: saving ? "progress" : "text",
											minWidth: 260,
											maxWidth: "100%",
										}}
										onFocus={(e) => {
											e.currentTarget.style.borderBottomColor =
												"var(--color-dashboard-accent)";
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderBottomColor =
												"var(--color-border-default)";
										}}
									/>
								</h1>
								{reviewStatus === "pending_review" ? (
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 13,
											color: "var(--color-dashboard-muted)",
											marginTop: 8,
										}}
									>
										{t("designerPortfolio.review.pendingHint")}
									</p>
								) : null}
								{reviewStatus === "rejected" ? (
									<div
										role="note"
										style={{
											marginTop: 12,
											padding: "12px 16px",
											borderRadius: 8,
											border:
												"1px solid var(--color-dashboard-danger-border-strong)",
											background: "var(--color-dashboard-danger-bg)",
											fontFamily: "var(--font-body)",
											maxWidth: 640,
										}}
									>
										<p
											style={{
												fontSize: 12,
												fontWeight: 600,
												color: "var(--color-dashboard-danger)",
											}}
										>
											{t("designerPortfolio.review.rejectedTitle")}
										</p>
										{reviewNotes ? (
											<p
												style={{
													fontSize: 13.5,
													color: "var(--color-dashboard-text)",
													marginTop: 4,
													whiteSpace: "pre-wrap",
												}}
											>
												{reviewNotes}
											</p>
										) : null}
										<p
											style={{
												fontSize: 12,
												color: "var(--color-dashboard-muted)",
												marginTop: 6,
											}}
										>
											{t("designerPortfolio.review.rejectedHint")}
										</p>
									</div>
								) : null}
							</div>
							<div
								style={{
									display: "flex",
									gap: 10,
									flexWrap: "wrap",
									alignSelf: "stretch",
									justifyContent: "flex-end",
								}}
							>
								<StudioButton
									type="button"
									variant="secondary"
									size="md"
									onClick={handleSave}
									disabled={saving || publishing}
								>
									Save Portfolio
								</StudioButton>
								{reviewStatus === "draft" || reviewStatus === "rejected" ? (
									<StudioButton
										type="button"
										variant="primary"
										size="md"
										onClick={handleSubmitForReview}
										disabled={publishing || saving}
										icon={Rocket}
									>
										{t(
											reviewStatus === "rejected"
												? "designerPortfolio.review.resubmit"
												: "designerPortfolio.review.submit",
										)}
									</StudioButton>
								) : (
									<StudioButton
										type="button"
										variant="quiet"
										size="md"
										onClick={() =>
											setConfirmRetract(
												reviewStatus === "pending_review"
													? "withdraw"
													: "unpublish",
											)
										}
										disabled={publishing || saving}
									>
										{t(
											reviewStatus === "pending_review"
												? "designerPortfolio.review.withdraw"
												: "designerPortfolio.review.unpublish",
										)}
									</StudioButton>
								)}
							</div>
						</div>
					</div>
				</FloatPanel>

				<AlertDialog
					open={confirmRetract !== null}
					onOpenChange={(open) => {
						if (!open) setConfirmRetract(null);
					}}
				>
					{confirmRetract ? (
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t(
										confirmRetract === "withdraw"
											? "designerPortfolio.review.withdrawConfirmTitle"
											: "designerPortfolio.review.unpublishConfirmTitle",
									)}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t(
										confirmRetract === "withdraw"
											? "designerPortfolio.review.withdrawConfirmBody"
											: "designerPortfolio.review.unpublishConfirmBody",
									)}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
								<AlertDialogAction
									onClick={() => void handleRetract(confirmRetract)}
								>
									{t(
										confirmRetract === "withdraw"
											? "designerPortfolio.review.withdraw"
											: "designerPortfolio.review.unpublish",
									)}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					) : null}
				</AlertDialog>

				<div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]">
					{/* Template Picker */}
					<FloatPanel elevation={1} padding={28}>
						<div className="space-y-6">
							<div>
								<Eyebrow>CHOOSE DESIGN TEMPLATE</Eyebrow>
								<h3
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 20,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
										marginTop: 4,
									}}
								>
									Choose template
								</h3>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
										marginTop: 2,
									}}
								>
									Choose one of {PORTFOLIO_TEMPLATES.length} layouts for your
									public portfolio page.
								</p>
							</div>

							<div className="grid gap-6 sm:grid-cols-2">
								{PORTFOLIO_TEMPLATES.map((tpl) => (
									<TemplateCard
										key={tpl.id}
										id={tpl.id}
										title={tpl.name}
										description={tpl.description}
										selected={template === tpl.id}
										projects={projects}
										onClick={() => setTemplate(tpl.id)}
									/>
								))}
							</div>

							<TemplateDeltaNotice
								savedTemplate={savedTemplate}
								selectedTemplate={template}
							/>
						</div>
					</FloatPanel>

					{/* Live Preview Panel */}
					<FloatPanel elevation={1} padding={28}>
						<div className="space-y-6">
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div>
									<Eyebrow>LIVE VISUAL QA</Eyebrow>
									<h3
										style={{
											fontFamily: "var(--font-display)",
											fontSize: 20,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
											marginTop: 4,
										}}
									>
										Live layout preview
									</h3>
								</div>
								<SelectedTemplateBadge template={template} />
							</div>

							<div
								style={{
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 11,
									overflow: "hidden",
									background: "var(--color-dashboard-paper)",
								}}
							>
								{renderLiveTemplatePreview(template, projects)}
							</div>

							<ViewLayoutHint template={template} />

							{reviewStatus === "approved" && slug ? (
								<div
									style={{
										borderTop: "1px solid var(--color-dashboard-border-soft)",
										paddingTop: 20,
									}}
									className="space-y-4"
								>
									<div>
										<Eyebrow>SHARING OPTIONS</Eyebrow>
										<p
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 12.5,
												color: "var(--color-dashboard-muted)",
												marginTop: 2,
											}}
										>
											Your portfolio is live and public at this URL.
										</p>
									</div>

									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 8,
											background: "var(--color-dashboard-paper)",
											border: "1px solid var(--color-dashboard-border)",
											borderRadius: 8,
											padding: "10px 14px",
										}}
									>
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 13,
												color: "var(--color-dashboard-text)",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												flex: 1,
											}}
										>
											{sharePath}
										</span>
										<button
											type="button"
											onClick={copyShareLink}
											aria-label="Copy share link"
											style={{
												color: "var(--color-dashboard-accent-dark)",
												border: "none",
												background: "none",
												cursor: "pointer",
												padding: 4,
											}}
										>
											<Copy className="h-4 w-4" />
										</button>
									</div>

									<div style={{ display: "flex", gap: 12, marginTop: 12 }}>
										<StudioButton
											type="button"
											variant="primary"
											size="sm"
											icon={MessageCircle}
											onClick={() => {
												const message = `I shared a portfolio: ${portfolioTitle}\nPlease open it inside Finishd.\n${sharePath}`;
												const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
												window.open(
													whatsappUrl,
													"_blank",
													"noopener,noreferrer",
												);
											}}
											style={{ flex: 1 }}
										>
											WhatsApp
										</StudioButton>
										<StudioButton
											type="button"
											variant="quiet"
											size="sm"
											icon={ExternalLink}
											onClick={() =>
												window.open(sharePath, "_blank", "noopener,noreferrer")
											}
											style={{ flex: 1 }}
										>
											Open Link
										</StudioButton>
									</div>
								</div>
							) : null}
						</div>
					</FloatPanel>
				</div>

				{/* Projects Section Header */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						borderBottom: "1px solid var(--color-dashboard-border)",
						paddingBottom: 16,
						marginTop: 16,
					}}
				>
					<div>
						<Eyebrow>PROJECTS LISTING</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 28,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.015em",
								margin: "4px 0 0",
							}}
						>
							Portfolio projects
						</h2>
					</div>
					<StudioButton
						type="button"
						variant="secondary"
						size="sm"
						icon={Plus}
						onClick={addProject}
					>
						Add Project Card
					</StudioButton>
				</div>

				{/* Projects Cards List */}
				{projects.map((project, index) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: projects array ordering is locally managed by state indexes
						key={index}
						className="space-y-4"
						data-testid={`portfolio-project-card-${index}`}
					>
						<FloatPanel elevation={1} padding={28}>
							<div className="space-y-6">
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										borderBottom:
											"1px solid var(--color-dashboard-border-soft)",
										paddingBottom: 14,
									}}
								>
									<span
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 13,
											fontWeight: 600,
											color: "var(--color-dashboard-accent-dark)",
										}}
									>
										PROJECT CARD #{index + 1}
									</span>
									<div style={{ display: "flex", gap: 12 }}>
										<ReorderProjectButtons
											index={index}
											canMoveUp={index > 0}
											canMoveDown={index < projects.length - 1}
											onMoveUp={() => moveProject(index, "up")}
											onMoveDown={() => moveProject(index, "down")}
										/>
										<button
											type="button"
											aria-label={`Delete project ${index + 1}`}
											onClick={() => removeProject(index)}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												width: 32,
												height: 32,
												borderRadius: 6,
												border: "1px solid var(--color-dashboard-border)",
												background: "var(--color-dashboard-paper)",
												cursor: "pointer",
												color: "var(--color-dashboard-pill-risk)",
											}}
										>
											<Trash2 className="h-4 w-4" />
										</button>
									</div>
								</div>

								<div className="grid gap-6 md:grid-cols-2">
									<div className="space-y-4">
										<div className="space-y-1.5">
											<span
												style={{
													fontSize: 13,
													fontWeight: 500,
													color: "var(--color-dashboard-muted-dark)",
													display: "block",
												}}
											>
												Project Title
											</span>
											<input
												type="text"
												value={project.title}
												onChange={(e) =>
													updateProject(index, { title: e.target.value })
												}
												placeholder="2BHK Modern Apartment"
												style={{
													width: "100%",
													padding: "10px 14px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
												}}
											/>
										</div>

										<div className="space-y-1.5">
											<span
												style={{
													fontSize: 13,
													fontWeight: 500,
													color: "var(--color-dashboard-muted-dark)",
													display: "block",
												}}
											>
												Short Summary
											</span>
											<input
												type="text"
												value={project.shortDescription}
												onChange={(e) =>
													updateProject(index, {
														shortDescription: e.target.value,
													})
												}
												placeholder="Brief one-line summary"
												style={{
													width: "100%",
													padding: "10px 14px",
													border: "1px solid var(--color-border-default)",
													borderRadius: 7,
													background: "var(--color-dashboard-paper)",
													fontSize: 14,
													color: "var(--color-dashboard-text)",
													outline: "none",
												}}
											/>
										</div>

										<div className="grid gap-4 sm:grid-cols-2">
											<div className="space-y-1.5">
												<span
													style={{
														fontSize: 13,
														fontWeight: 500,
														color: "var(--color-dashboard-muted-dark)",
														display: "block",
													}}
												>
													Category
												</span>
												<input
													type="text"
													value={project.category || ""}
													onChange={(e) =>
														updateProject(index, { category: e.target.value })
													}
													placeholder="e.g. Residential"
													style={{
														width: "100%",
														padding: "10px 14px",
														border: "1px solid var(--color-border-default)",
														borderRadius: 7,
														background: "var(--color-dashboard-paper)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
														outline: "none",
													}}
												/>
											</div>

											<div className="space-y-1.5">
												<span
													style={{
														fontSize: 13,
														fontWeight: 500,
														color: "var(--color-dashboard-muted-dark)",
														display: "block",
													}}
												>
													Completion Date
												</span>
												<input
													type="date"
													value={project.completionDate || ""}
													onChange={(e) =>
														updateProject(index, {
															completionDate: e.target.value,
														})
													}
													style={{
														width: "100%",
														padding: "10px 14px",
														border: "1px solid var(--color-border-default)",
														borderRadius: 7,
														background: "var(--color-dashboard-paper)",
														fontSize: 14,
														color: "var(--color-dashboard-text)",
														outline: "none",
													}}
												/>
											</div>
										</div>
									</div>

									<div className="space-y-1.5">
										<span
											style={{
												fontSize: 13,
												fontWeight: 500,
												color: "var(--color-dashboard-muted-dark)",
												display: "block",
											}}
										>
											Full Description
										</span>
										<textarea
											value={project.fullDescription || ""}
											onChange={(e) =>
												updateProject(index, {
													fullDescription: e.target.value,
												})
											}
											placeholder="Detailed project story"
											rows={4}
											style={{
												width: "100%",
												padding: "10px 14px",
												border: "1px solid var(--color-border-default)",
												borderRadius: 7,
												background: "var(--color-dashboard-paper)",
												fontSize: 14,
												color: "var(--color-dashboard-text)",
												outline: "none",
												resize: "vertical",
											}}
										/>
									</div>

									<ThumbnailUpload
										label="Project Thumbnail"
										currentUrl={project.thumbnailUrl}
										onUpload={(url: string) =>
											updateProject(index, { thumbnailUrl: url })
										}
										onRemove={() => updateProject(index, { thumbnailUrl: "" })}
									/>

									<div className="mt-4">
										<MultiImageUpload
											label="Project Images"
											currentUrls={project.imageUrls}
											onUpload={(urls) =>
												updateProject(index, {
													imageUrls: [...project.imageUrls, ...urls],
												})
											}
											onRemove={(imgIndex) => {
												const next = project.imageUrls.filter(
													(_, i) => i !== imgIndex,
												);
												updateProject(index, { imageUrls: next });
											}}
											maxImages={12}
										/>
									</div>
								</div>
							</div>
						</FloatPanel>
					</div>
				))}
			</div>
		</div>
	);
}
